import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useAbTesting } from '@/contexts/abtesting-context/abtesting-context'
import { eventKeys } from '@/contexts/abtesting-context/optimizely-event-keys'
import { useAnalytics } from '@/hooks/use-analytics'
import { EVENT_LABEL_DESELECT_UNIT, EVENT_LABEL_SELECT_UNIT } from '@/lib'
import { StoreData } from '@/types'

import {
  storeNameToUrlParam,
  useSelectedStoresFromUrl,
} from '../product-list/use-selected-stores-from-url'
import { useStoresContext } from './stores-context'

function storeDataListToUrlParam(storeDataList: StoreData[]) {
  const param = storeDataList.map(({ name }) => name).map(storeNameToUrlParam)
  // sort params alphabetically
  param.sort(
    (a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }) // case-insensitive
  )
  return param.join('-')
}

export function useSelectedStores() {
  const navigate = useNavigate()
  const { getStoreById } = useStoresContext()
  const { selectedStores, selectedStoreIds } = useSelectedStoresFromUrl()
  const { sendCustomEvent } = useAnalytics()
  const optimizely = useAbTesting()
  const { t } = useTranslation()

  const selectedStoresLabel =
    selectedStores.length > 0
      ? `${t(`stores.${selectedStores[0].name}`, { defaultValue: selectedStores[0].name })}${
          selectedStores.length > 1 ? ` +${selectedStores.length - 1}` : ``
        }`
      : t('product-list-page.filters.select-store-label') // TODO: Replace in Phrase?

  function toggleStore(storeId: string) {
    const selectedStore = getStoreById(storeId)
    if (!selectedStore) return
    if (selectedStoreIds.includes(storeId)) {
      const newSelectedStores = storeDataListToUrlParam(
        selectedStores.filter(
          (selectedStore) => selectedStore.store_id !== storeId
        )
      )
      void sendCustomEvent({
        event_label: EVENT_LABEL_DESELECT_UNIT(selectedStore.name),
        event_action: selectedStore.name,
      })
      optimizely.track(eventKeys.select_unit)
      navigate(`/${newSelectedStores}`)
    } else {
      const newSelectedStores = storeDataListToUrlParam([
        ...selectedStores,
        selectedStore,
      ])
      void sendCustomEvent({
        event_label: EVENT_LABEL_SELECT_UNIT(selectedStore.name),
        event_action: selectedStore.name,
      })
      optimizely.track(eventKeys.select_unit_manually)
      navigate(`/${newSelectedStores}`)
    }
  }

  function clearStores() {
    navigate(`/`)
  }

  return {
    selectedStores,
    selectedStoresLabel,
    toggleStore,
    clearStores,
  }
}
